// frontend/src/components/ApiErrorHandler.js
import React, { useState } from 'react';

const ApiErrorHandler = ({ error, onClose, onSwitchModel }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!error) return null;

  // Parse error details
  const parseError = (errorData) => {
    let type = 'general';
    let model = 'Unknown';
    let dailyLimit = 20;
    let retrySeconds = null;
    let availableModels = [];

    if (errorData?.status === 429) {
      type = 'quota';
      const errorMessage = errorData?.data?.details || errorData?.message || '';
      
      // Extract model name
      const modelMatch = errorMessage.match(/model: (\S+)/i) || 
                        errorMessage.match(/for model "?([^"\s]+)"?/i);
      if (modelMatch) model = modelMatch[1];
      
      // Extract daily limit
      const limitMatch = errorMessage.match(/limit: (\d+)/i);
      if (limitMatch) dailyLimit = parseInt(limitMatch[1]);
      
      // Extract retry time
      const retryMatch = errorMessage.match(/retry in (\d+\.?\d*)s/i);
      if (retryMatch) retrySeconds = parseFloat(retryMatch[1]);
      
      // Get available models from response
      if (errorData.data?.availableModels) {
        availableModels = errorData.data.availableModels;
      }
    }

    return { type, model, dailyLimit, retrySeconds, availableModels };
  };

  const { type, model, dailyLimit, retrySeconds, availableModels } = parseError(error);

  const formatTime = (seconds) => {
    if (!seconds) return 'soon';
    if (seconds < 60) return `${Math.ceil(seconds)} seconds`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  const getIcon = () => {
    switch(type) {
      case 'quota': return '⏳';
      case 'auth': return '🔐';
      default: return '⚠️';
    }
  };

  const getTitle = () => {
    switch(type) {
      case 'quota': return 'Daily Limit Reached';
      case 'auth': return 'Session Expired';
      default: return 'Service Unavailable';
    }
  };

  const getDescription = () => {
    switch(type) {
      case 'quota':
        return `You've used all ${dailyLimit} requests for ${model} today. ${
          retrySeconds ? `Please try again in ${formatTime(retrySeconds)}.` : ''
        }`;
      case 'auth':
        return 'Your session has expired. Please login again to continue.';
      default:
        return 'The service is temporarily unavailable. Please try again shortly.';
    }
  };

  return (
    <div className="api-error-overlay">
      <div className="api-error-card">
        <div className="error-header">
          <div className="error-icon">{getIcon()}</div>
          <h3>{getTitle()}</h3>
          <button 
            className="error-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="error-body">
          <p className="error-description">{getDescription()}</p>

          {type === 'quota' && (
            <div className="solutions-section">
              <h4>What you can do:</h4>
              <div className="solutions-grid">
                <div className="solution-card">
                  <div className="solution-icon">🔄</div>
                  <div className="solution-content">
                    <h5>Switch Models</h5>
                    <p>Use a different AI model with available requests</p>
                    {availableModels.length > 0 && (
                      <div className="model-options">
                        {availableModels.slice(0, 3).map((modelName, idx) => (
                          <button
                            key={idx}
                            className="model-tag"
                            onClick={() => onSwitchModel?.(modelName)}
                          >
                            {modelName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="solution-card">
                  <div className="solution-icon">⏰</div>
                  <div className="solution-content">
                    <h5>Wait & Retry</h5>
                    <p>Limits reset daily. Try again {retrySeconds ? formatTime(retrySeconds) : 'later'}.</p>
                    {retrySeconds && (
                      <div className="retry-timer">
                        <div className="timer-progress">
                          <div 
                            className="timer-fill" 
                            style={{ width: '100%' }}
                          />
                        </div>
                        <span>Retry in {Math.ceil(retrySeconds)}s</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="solution-card">
                  <div className="solution-icon">📊</div>
                  <div className="solution-content">
                    <h5>Check Usage</h5>
                    <p>Monitor your remaining requests and plan usage</p>
                    <button 
                      className="btn btn-outline"
                      onClick={() => window.open('https://ai.dev/usage', '_blank')}
                    >
                      View Usage Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="error-actions">
            {type === 'quota' && availableModels.length > 0 && (
              <button 
                className="btn btn-primary"
                onClick={() => onSwitchModel?.(availableModels[0])}
              >
                Switch to {availableModels[0]}
              </button>
            )}
            
            {type === 'auth' && (
              <button 
                className="btn btn-primary"
                onClick={() => window.location.href = '/login'}
              >
                Go to Login
              </button>
            )}
            
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>

            <button 
              className="btn btn-text"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Hide Details' : 'Show Technical Details'}
            </button>
          </div>

          {isExpanded && error.message && (
            <div className="technical-details">
              <h5>Technical Details:</h5>
              <pre>{typeof error.message === 'string' ? error.message : JSON.stringify(error.message, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiErrorHandler;